/** Iter 110 — draggable floating action button hook.
 * Pointer-based drag with tap detection (<8px = tap), edge snapping,
 * viewport clamping that reserves space for the mobile bottom nav +
 * iOS safe area, and localStorage persistence. */
import { useCallback, useEffect, useRef, useState } from "react";

const BTN = 56;

function bottomReserve(extra = 0) {
  const safe = Number(getComputedStyle(document.documentElement).getPropertyValue("--sat-b") || 0);
  // < lg screens have the fixed bottom nav (~64px) → keep clear of it.
  return Math.max(window.innerWidth < 1024 ? 76 : 12, extra) + safe + 8;
}

function clamp(pos, extra = 0, elW = BTN) {
  const w = window.innerWidth, h = window.innerHeight;
  return {
    x: Math.min(Math.max(8, pos.x), w - elW - 8),
    y: Math.min(Math.max(72, pos.y), h - BTN - bottomReserve(extra)),
  };
}

export default function useDraggableFab(storageKey, defaultCorner = "left", extraReserve = 0) {
  const [pos, setPos] = useState(null);
  const drag = useRef({ active: false, moved: false, dx: 0, dy: 0 });
  const widthRef = useRef(BTN);
  const suppressClick = useRef(false);

  useEffect(() => {
    const compute = () => {
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem(storageKey) || "null"); } catch { /* ignore */ }
      const def = {
        x: defaultCorner === "left" ? 16 : window.innerWidth - BTN - 16,
        y: window.innerHeight - BTN - bottomReserve(extraReserve),
      };
      setPos(clamp(saved || def, extraReserve));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [storageKey, defaultCorner, extraReserve]);

  const onPointerDown = useCallback((e) => {
    widthRef.current = e.currentTarget?.offsetWidth || BTN;
    drag.current = { active: true, moved: false, dx: e.clientX, dy: e.clientY, ox: null };
    const onMove = (ev) => {
      if (!drag.current.active) return;
      const mx = ev.clientX - drag.current.dx;
      const my = ev.clientY - drag.current.dy;
      if (!drag.current.moved && Math.hypot(mx, my) < 8) return;
      drag.current.moved = true;
      ev.preventDefault();
      setPos((p) => {
        if (drag.current.ox == null) { drag.current.ox = p.x; drag.current.oy = p.y; }
        return clamp({ x: drag.current.ox + mx, y: drag.current.oy + my }, extraReserve, widthRef.current);
      });
    };
    const onUp = () => {
      const wasDrag = drag.current.moved;
      drag.current.active = false;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (wasDrag) {
        suppressClick.current = true;
        setTimeout(() => { suppressClick.current = false; }, 150);
        setPos((p) => {
          // Snap to the nearest horizontal edge and persist.
          const elW = widthRef.current;
          const snapped = clamp({ x: p.x + elW / 2 < window.innerWidth / 2 ? 16 : window.innerWidth - elW - 16, y: p.y }, extraReserve, elW);
          try { localStorage.setItem(storageKey, JSON.stringify(snapped)); } catch { /* ignore */ }
          return snapped;
        });
      }
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
  }, [storageKey, extraReserve]);

  const wasDragged = () => suppressClick.current;
  const style = pos ? { position: "fixed", left: pos.x, top: pos.y, bottom: "auto", right: "auto", touchAction: "none" } : { visibility: "hidden" };
  return { style, onPointerDown, wasDragged };
}
